// Food — pill toggle between Shopping / Recipes / Meal plan, with
// Shopping as the default. Recipes and Meal plan render placeholder
// stubs until their dedicated ports land.

import { useEffect, useRef, useState } from "preact/hooks";
import "../../styles/food.css";
import type { NotifyFn } from "../../components/toast";

import { Icon } from "../../icons";
import { Shopping } from "./shopping";
import { Pantry } from "./pantry";
import { Recipes } from "./recipes";
import { MealPlan } from "./meal-plan";
import { useHouseholdSize } from "../../lib/use-household";

type FoodMode = "shopping" | "pantry" | "recipes" | "meal";

interface FoodViewProps {
  notify: NotifyFn;
  /** Cross-section deep link from the global search palette — switches
   *  the sub-tab to recipes / shopping / meal-plan instead of always
   *  landing on the default shopping pane. Parent clears via
   *  onConsumeInitialMode so a later natural visit doesn't re-route. */
  initialMode?: FoodMode | null;
  onConsumeInitialMode?: () => void;
  /** Voice navigation deep-link — when set, force `mode` to "recipes"
   *  and pop the detail panel for this recipe id on mount. Parent
   *  clears via onConsumeInitialRecipeId. */
  initialRecipeId?: string | number | null;
  onConsumeInitialRecipeId?: () => void;
}

export function FoodView({ notify, initialMode, onConsumeInitialMode, initialRecipeId, onConsumeInitialRecipeId }: FoodViewProps) {
  const [mode, setMode] = useState<FoodMode>(initialMode || "shopping");
  // Consume an initialMode hint that landed AFTER mount — happens
  // when the user is already on the food tab and picks a recipe-or-
  // shopping result from the global palette. Sync to internal state
  // and tell the parent to clear so we don't get pinned in this mode.
  useEffect(() => {
    if (initialMode && initialMode !== mode) setMode(initialMode);
    if (initialMode) onConsumeInitialMode?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);
  const segRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 4, width: 0 });
  // Cross-section deep link: when a recipe-linked meal-plan cell is
  // tapped, MealPlan calls openRecipe(id). We switch to the recipes
  // section and pass the id down so Recipes can open the detail
  // panel on mount. Recipes calls back to clear the pending id so a
  // later natural visit doesn't re-pop the panel.
  const [pendingRecipeId, setPendingRecipeId] = useState<string | number | null>(null);
  // Voice nav: if the parent handed us a recipe id, force the recipes
  // sub-tab and queue the detail panel to open via pendingRecipeId.
  useEffect(() => {
    if (initialRecipeId == null) return;
    setMode("recipes");
    setPendingRecipeId(initialRecipeId);
    onConsumeInitialRecipeId?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecipeId]);
  // Household size powers auto-scale in Recipes + cook mode. Lifted
  // to the parent so all three sub-views read the same value and the
  // setter from the header pill updates Recipes mid-session without
  // a remount.
  const household = useHouseholdSize();
  const [householdEditOpen, setHouseholdEditOpen] = useState(false);
  const [householdDraft, setHouseholdDraft] = useState<string>("");

  // Slide the pill indicator under the active mode. Recompute on
  // mode change AND on resize so the indicator stays aligned when
  // the viewport reflows.
  useEffect(() => {
    const reposition = () => {
      const seg = segRef.current;
      if (!seg) return;
      const active = seg.querySelector('[data-active="true"]') as HTMLElement | null;
      if (!active) return;
      const segRect = seg.getBoundingClientRect();
      const r = active.getBoundingClientRect();
      setIndicator({ left: r.left - segRect.left, width: r.width });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [mode]);

  return (
    <div className="food-page">
      <div className="food-head">
        <div>
          <div className="eyebrow">Kitchen</div>
          <h1 className="h-display">
            Food <span className="h-italic" style={{ color: "var(--accent)" }}>&amp; what to eat</span>
          </h1>
        </div>
        <div className="pill-seg" ref={segRef}>
          <span className="pill-indicator" style={{ left: indicator.left, width: indicator.width }} />
          {/* Labels wrapped in <span> so the @media (max-width: 520px)
            * rule in shell.css can hide them and collapse to icon-only
            * — needed since the 4-pill row overflows phone widths. */}
          <button data-active={mode === "shopping"} onClick={() => setMode("shopping")} type="button" title="Shopping">
            <Icon name="food" size={14} /> <span>Shopping</span>
          </button>
          <button data-active={mode === "pantry"} onClick={() => setMode("pantry")} type="button" title="Pantry">
            <Icon name="database" size={14} /> <span>Pantry</span>
          </button>
          <button data-active={mode === "recipes"} onClick={() => setMode("recipes")} type="button" title="Recipes">
            <Icon name="flame" size={14} /> <span>Recipes</span>
          </button>
          <button data-active={mode === "meal"} onClick={() => setMode("meal")} type="button" title="Meal plan">
            <Icon name="calendar" size={14} /> <span>Meal plan</span>
          </button>
        </div>
        <button
          className="food-household-pill"
          onClick={() => { setHouseholdDraft(String(household.size || "")); setHouseholdEditOpen(true); }}
          title="Set household size — recipes auto-scale to this"
          type="button"
        >
          <Icon name="people" size={13} />
          <span>
            {household.size ? `Feeds ${household.size}` : "Set household"}
          </span>
        </button>
      </div>

      {mode === "shopping" && <Shopping notify={notify} />}
      {mode === "pantry" && <Pantry notify={notify} />}
      {mode === "recipes" && (
        <Recipes
          notify={notify}
          openRecipeId={pendingRecipeId}
          onOpenRecipeConsumed={() => setPendingRecipeId(null)}
          householdSize={household.size}
        />
      )}
      {mode === "meal" && (
        <MealPlan
          notify={notify}
          onOpenRecipe={(id) => {
            setPendingRecipeId(id);
            setMode("recipes");
          }}
          householdSize={household.size}
        />
      )}

      {householdEditOpen && (
        <div className="hh-modal-bg" onClick={() => setHouseholdEditOpen(false)}>
          <div className="hh-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Household size">
            <h3 className="hh-title">How many people do you cook for?</h3>
            <p className="hh-sub">
              Recipes auto-scale to this. We use it together with each recipe's "serves N" to figure out the multiplier.
            </p>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={20}
              className="hh-input"
              value={householdDraft}
              autofocus
              onInput={(e) => setHouseholdDraft((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const n = Number(householdDraft);
                  if (Number.isFinite(n) && n >= 1 && n <= 99) {
                    household.set(Math.floor(n));
                    setHouseholdEditOpen(false);
                  }
                }
              }}
              placeholder="e.g. 4"
            />
            <div className="hh-actions">
              {household.size != null && (
                <button
                  className="btn-ghost"
                  type="button"
                  onClick={() => { household.set(null); setHouseholdEditOpen(false); }}
                >
                  Clear
                </button>
              )}
              <span style={{ flex: 1 }} />
              <button className="btn-ghost" type="button" onClick={() => setHouseholdEditOpen(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  const n = Number(householdDraft);
                  if (Number.isFinite(n) && n >= 1 && n <= 99) {
                    household.set(Math.floor(n));
                    setHouseholdEditOpen(false);
                  }
                }}
                disabled={!householdDraft || !Number.isFinite(Number(householdDraft)) || Number(householdDraft) < 1 || Number(householdDraft) > 99}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function _ComingSoon({ what }: { what: string }) {
  return (
    <div style={{ textAlign: "center", padding: "80px 20px", color: "var(--text-dim)" }}>
      <div className="h-display" style={{ fontSize: 32, color: "var(--text)", margin: 0 }}>{what}</div>
      <p className="h-italic" style={{ fontSize: 16, marginTop: 8 }}>
        Coming in the next pass — Shopping is the only one wired so far.
      </p>
    </div>
  );
}
